import { WalletIcon } from "assets/icons/WalletIcon";
import ChevronBack from "assets/svgComponents/buyTokenForm/ChevronBack";
import CopyIcon from "assets/svgComponents/buyTokenForm/CopyIcon";
import InvestNowButton from "assets/svgComponents/buyTokenForm/InvestNowButton";
import ArrowForwardColoredIcon from "assets/svgComponents/PresaleBlock/ArrowForwardColoredIcon";
import { ConnectWallet } from "assets/svgComponents/PresaleBlock/ConnectWallet";
import { InfoIcon } from "assets/svgComponents/PresaleBlock/InfoIcon";
import clsx from "clsx";
import { Loader } from "components/Loader";
import { NetworkKey, PaymentMethod, networkData } from "constants/methods";
import { useCopyToClipboard } from "hooks/useCopyToClipboard";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  gradientRowBgClass,
  investButtonClass,
  shadowAndBorderClass,
} from "screens/Dashboard/utils/classes";
import { authApi } from "services/auth";
import { tokensApi } from "services/tokens";
import { appStore } from "store";
import { Button } from "ui-kit/Button";
import { calcAmountOfTokensToShow, truncateMiddle } from "utils";
import { useAccount } from "wagmi";
import { PaymentFormType, Step } from "./scheme";
import { AttsIcon } from "assets/svgComponents/PresaleBlock/ConnectWallet copy";

interface SendDepositProps {
  paymentInUSD: number;
  preselectedMethod: PaymentMethod;
  setIsPaymentDialogOpen: Dispatch<SetStateAction<boolean>>;
  referralCode: string;
  handleChangeStep: (step: Step) => void;
  promoCodePercentage: number;
}

export function ConfirmStep({
  paymentInUSD,
  preselectedMethod,
  setIsPaymentDialogOpen,
  referralCode,
  handleChangeStep,
  promoCodePercentage,
}: SendDepositProps) {
  const form = useFormContext<PaymentFormType>();
  const { currentRound, currentBonus, currentReferralProgram } = appStore();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);

  const { address: connectedWalletAddress } = useAccount();

  const step = form.watch("step");

  if (!form) {
    throw new Error("This component must be used within a <FormProvider />");
  }

  const tokens = calcAmountOfTokensToShow(
    paymentInUSD,
    currentRound,
    referralCode,
    promoCodePercentage,
  );

  const toensWithoutBonus = calcAmountOfTokensToShow(
    paymentInUSD,
    currentRound,
    false,
  );

  const [copiedText, copyToClipboard] = useCopyToClipboard(2000);
  const handleCopy = () => {
    copyToClipboard(String(connectedWalletAddress));
    toast.success("Address copied!");
  };

  const referralBonusToShow =
    (Number(tokens) - Number(toensWithoutBonus)).toFixed(2) || "0";

  return (
    <>
      {" "}
      <div
        className={clsx(
          "flex flex-col max-lg:gap-[8px] overflow-hidden sm:min-h-auto rounded-[28px] max-lg:rounded-[28px] max-lg:before:!rounded-[28px] before:!rounded-[28px] bg-[#111111] p-[12px] max-lg:p-[12px] pb-[26px] w-[445px] max-lg:w-full",
          shadowAndBorderClass,
        )}
      >
        <div
          className={clsx(
            "flex items-center justify-between rounded-[16px] max-lg:rounded-[12px] mb-[20px] before:!rounded-[16px] max-lg:before:!rounded-[12px] p-[8px] max-lg:p-[8px]",
            gradientRowBgClass,
          )}
        >
          <div className="flex items-center gap-[16px] max-lg:gap-[12px]">
            <button
              className={clsx(
                "w-[31px] h-[31px] max-lg:w-[31px] max-lg:h-[31px] bg-[#252525] text-white hover:scale-[1.1] cursor-pinter rounded-full flex items-center justify-center flex-shrink-0",
                shadowAndBorderClass,
              )}
              onClick={() => setIsPaymentDialogOpen(false)}
            >
              <ChevronBack className="max-lg:w-[23px] max-lg:h-[23px]" />
            </button>
            <h2 className="text-[20px] max-lg:text-16m font-luxora-medium leading-[110%] text-white">
              {t("Purchase Details")}
            </h2>
          </div>

          <div
            className={clsx(
              "w-[88px] max-lg:w-[88px] h-[31px] max-lg:h-[31px] flex items-center justify-center rounded-[8px] max-lg:rounded-[8px] before:!rounded-[8px] max-lg:before:!rounded-[8px] bg-[#252525]",
              shadowAndBorderClass,
            )}
          >
            <div className="text-[16px] max-lg:text-[16px] text-white">
              {" "}
              Step 1/3
            </div>
          </div>
        </div>

        <div
          className={clsx(
            "bg-[#1a1a1a] max-lg:flex hidden gap-[12px] max-lg:gap-[12px] py-[8px] max-lg:py-[8px] px-[12px] max-lg:px-[12px] rounded-[16px] max-lg:rounded-[16px] before:!rounded-[16px] max-lg:before:!rounded-[16px]",
            shadowAndBorderClass,
          )}
        >
          <InfoIcon className="w-[24px] max-lg:w-[24px] h-[24px] max-lg:h-[24px] min-w-[24px] max-lg:min-w-[24px] min-h-[24px] max-lg:min-h-[24px]" />
          <div className="text-[16px] max-lg:text-[16px] text-white/40 leading-[110%] font-[400] max-lg:font-[400]">
            You can use any wallet to purchase $ATTS, but wallet you linked will
            be used to receive purchased $ATTS
          </div>
        </div>

        <div
          className={clsx(
            "bg-[#1a1a1a] p-[8px] max-lg:p-[8px] rounded-[16px] max-lg:rounded-[16px] before:!rounded-[16px] max-lg:before:!rounded-[16px]",
            shadowAndBorderClass,
          )}
        >
          {" "}
          <div className="flex flex-col gap-[4px] max-lg:gap-[4px]">
            <div className="max-lg:pl-[4px] text-[14px] max-lg:text-14m font-luxora-regular text-left leading-[110%] text-white/40">
              {t("Wallet to receive $ATTS")}
            </div>
            <div className="flex items-center justify-between border-[2px] rounded-[16px] max-lg:rounded-[12px] max-lg:p-[4px] max-lg:pr-[16px] lg:py-[8px] lg:pl-[8px] lg:pr-[16px] border-[#252525]">
              <div className="flex items-center gap-2">
                {" "}
                <div className="bg-[#333333] max-lg:rounded-[8px] max-lg:w-[32px] max-lg:h-[32px] w-[32px] h-[32px] rounded-[8px] flex items-center justify-center">
                  {" "}
                  <ConnectWallet className="max-lg:w-[21px] max-lg:h-[21px]" />
                </div>
                <div
                  className="text-[16px] max-lg:text-16m font-luxora-regular leading-normal text-white truncate max-lg:max-w-[188px] max-w-[288px]"
                  title={connectedWalletAddress}
                >
                  {truncateMiddle(String(connectedWalletAddress), 9, 8)}
                </div>
              </div>
              <div
                onClick={handleCopy}
                className="bg-[#FFFFFF] cursor-pointer max-lg:w-[29px] max-lg:h-[29px] max-lg:rounded-[8px] shrink-0 w-[32px] h-[32px] rounded-[8px] flex items-center justify-center"
              >
                {" "}
                <CopyIcon className="max-lg:w-[15px] max-lg:h-[15px]" />
              </div>
            </div>
          </div>
          <div className="grid lg:grid-cols-2 max-lg:grid-cols-1 gap-2 max-lg:gap-[8px] mt-[16px] max-lg:mt-[16px]">
            <div className="flex flex-col gap-[4px] max-lg:gap-[4px]">
              <div className="max-lg:pl-[4px] text-[14px] max-lg:text-14m font-luxora-regular text-left leading-[110%] text-white/40">
                {t("You receive")}
              </div>
              <div className="flex items-center justify-between border-[2px] rounded-[16px] max-lg:rounded-[12px] max-lg:pt-[4px] max-lg:pr-[16px] lg:py-[3px] lg:pl-[3px] lg:pr-[16px] border-[#252525]">
                <div className="flex items-center gap-[1px]">
                  <AttsIcon className="size-[46px] max-lg:size-[39px]" />

                  <div className="text-[16px] max-lg:text-16m font-luxora-regular leading-normal text-white">
                    ATTS
                  </div>
                </div>
                <div
                  className={` max-w-[90px] max-lg:max-w-full ${
                    String(tokens).length < 10
                      ? "text-[16px] max-lg:text-16m"
                      : "text-[12px] max-lg:text-14m break-all"
                  }  font-luxora-regular leading-[110%] text-white`}
                >
                  {tokens}
                </div>
              </div>
            </div>
            <div className="flex flex-col gap-[4px] max-lg:gap-[4px]">
              <div className="max-lg:pl-[4px] text-[14px] max-lg:text-14m font-luxora-regular text-left leading-[110%] text-white/40">
                {t("heroForm.bonusAtts")}
              </div>
              <div className="flex items-center justify-between border-[2px] rounded-[16px] max-lg:rounded-[12px] max-lg:pt-[4px] max-lg:pr-[16px] lg:py-[3px] lg:pl-[3px] lg:pr-[16px] border-[#252525]">
                <div className="flex items-center gap-[1px]">
                  <AttsIcon className="size-[46px] max-lg:size-[39px]" />

                  <div className="text-[16px] max-lg:text-16m font-luxora-regular leading-[110%] text-white">
                    ATTS
                  </div>
                </div>
                <div
                  className={` max-w-[90px] max-lg:max-w-full ${
                    referralBonusToShow.length < 10
                      ? "text-[16px] max-lg:text-16m"
                      : "text-[12px] max-lg:text-14m break-all"
                  }  font-luxora-regular leading-[110%] text-white`}
                >
                  {referralBonusToShow || 0}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div
          className={clsx(
            "bg-[#1a1a1a] max-lg:hidden flex gap-[12px] max-lg:gap-[12px] py-[8px] max-lg:py-[8px] px-[12px] max-lg:px-[12px] mt-[12px] max-lg:mt-[12px] rounded-[16px] max-lg:rounded-[16px] before:!rounded-[16px] max-lg:before:!rounded-[16px]",
            shadowAndBorderClass,
          )}
        >
          <InfoIcon className="w-[24px] max-lg:w-[24px] h-[24px] max-lg:h-[24px] min-w-[24px] max-lg:min-w-[24px] min-h-[24px] max-lg:min-h-[24px]" />
          <div className="text-[16px] max-lg:text-[16px] text-white/40 leading-[110%] font-[400] max-lg:font-[400]">
            You can use any wallet to purchase $ATTS, but wallet you linked will
            be used to receive purchased $ATTS
          </div>
        </div>

        <div className="flex gap-[8px] mt-[144px] max-lg:mt-[75px]">
          <Button
            onClick={() => {
              handleChangeStep(Step.Payment);
            }}
            className={investButtonClass}
          >
            <div
              className={`absolute flex ${
                !isLoading && "hidden"
              } items-center justify-center w-full h-full
             top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 `}
            >
              {" "}
              <Loader />
            </div>

            <span className="text-black max-lg:text-16m text-[16px] leading-[110%] font-[500]">
              {" "}
              {t("heroForm.continueToPayment")}
            </span>
            <div className="bg-[#F3F3F8] max-lg:rounded-[8px] max-lg:w-[32px] max-lg:h-[32px] w-[32px] h-[32px] rounded-[8px] flex items-center justify-center">
              {" "}
              <ArrowForwardColoredIcon className="size-[18px]" />
            </div>
          </Button>
        </div>
      </div>
    </>
  );
}
